import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, Mark, Container, VideoSlot } from '../components/marketing/Primitives';
import { useVideo } from '../cms/ContentProvider';
import {
  videos,
  miniEncounter,
  twoIntelligences,
  encounterAnatomy,
  scoringPoints,
  caseAuthoring,
  honestScope,
  faq,
} from '../data/practicumsData';

/* The interactive specimen: a scripted miniature of the encounter mechanics.
   Three turns, three choices each. General questions get general answers;
   the discriminating question opens the gate. Then a separate "evaluator"
   scores the transcript against explicit key features, hurdles first. */
function MiniEncounter() {
  const [turn, setTurn] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [gates, setGates] = useState([]);
  const [ended, setEnded] = useState(false);

  const done = turn >= miniEncounter.turns.length;

  function choose(opt) {
    setTranscript((t) => [...t, { q: opt.text, a: opt.reply }]);
    if (opt.kind === 'gate') setGates((g) => [...g, opt.gate]);
    setTurn((n) => n + 1);
  }

  function reset() {
    setTurn(0);
    setTranscript([]);
    setGates([]);
    setEnded(false);
  }

  const features = miniEncounter.keyFeatures.map((kf) => ({ ...kf, met: kf.logic(gates) }));
  const essentialMissed = features.some((f) => (f.flag === 'essential' || f.flag === 'safety-critical') && !f.met);
  const earned = features.filter((f) => f.met).reduce((s, f) => s + f.points, 0);
  const total = features.reduce((s, f) => s + f.points, 0);

  return (
    <div className="specimen-card !rotate-0">
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[11px] font-semibold tracking-wide text-slate">Before you begin</p>
        <p className="text-[11px] text-faint">A scripted miniature; the product is spoken, on video</p>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-bright">{miniEncounter.doorInstruction}</p>

      {/* Transcript */}
      {transcript.length > 0 && (
        <div className="mt-4 max-h-48 space-y-2 overflow-y-auto rounded border border-hairline bg-ink-deep/60 p-3">
          {transcript.map((line, i) => (
            <div key={i}>
              <p className="text-[11px] text-faint">
                You: <span className="text-slate">{line.q}</span>
              </p>
              <p className="text-[12px] text-bright">
                {miniEncounter.patient.split(',')[0]}: <span className="font-normal">{line.a}</span>
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Choices, evaluation, or end state */}
      {!ended && !done && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-slate">
            Question {turn + 1} of {miniEncounter.turns.length}: what do you ask?
          </p>
          <div className="mt-2 space-y-2">
            {miniEncounter.turns[turn].options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => choose(opt)}
                className="w-full rounded border border-hairline px-3 py-2 text-left text-[12px] leading-snug text-slate transition-colors hover:border-seal/50 hover:text-bright"
              >
                {opt.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {!ended && done && (
        <button type="button" onClick={() => setEnded(true)} className="btn-primary mt-4 w-full !py-2.5">
          End session
        </button>
      )}

      {ended && (
        <div className="mt-4 rounded border border-hairline-dark bg-ink-deep/80 p-4">
          <p className="text-[11px] font-semibold text-bright">Evaluator scorecard</p>
          <p className="mt-1 text-[10px] text-faint">
            Read from the transcript by a separate intelligence. The patient never graded you.
          </p>

          {/* Hurdles first, the number last: the product's deliberate ordering. */}
          <p className={`mt-3 text-[12px] font-semibold ${essentialMissed ? 'text-rose-300' : 'text-seal'}`}>
            {essentialMissed
              ? 'Hurdle not met: an essential feature was missed, regardless of the percentage.'
              : 'Hurdles met.'}
          </p>

          <ul className="mt-3 space-y-2.5">
            {features.map((f) => (
              <li key={f.id} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px] ${
                    f.met ? 'border-seal text-seal' : 'border-rose-400/70 text-rose-300'
                  }`}
                >
                  {f.met ? '\u2713' : '\u2715'}
                </span>
                <div>
                  <p className="text-[11px] leading-snug text-slate">{f.label}</p>
                  <p className="mt-0.5 text-[10px] text-faint">
                    {f.flag === 'safety-critical' ? 'Safety-critical \u00b7 3 points' : 'Essential \u00b7 2 points'}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-3 border-t border-hairline-dark pt-2.5 text-[11px] text-slate">
            Key-feature score: <span className="font-semibold text-bright">{earned} of {total}</span>
            <span className="ml-2 text-faint">(the number comes last, on purpose)</span>
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-3 text-[12px] font-medium text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
          >
            Try the station again
          </button>
        </div>
      )}
    </div>
  );
}

export default function PracticumsPage() {
  const [openFaq, setOpenFaq] = useState(null);

  // Published from the content console at /admin; falls back to the film
  // configured in src/data/practicumsData.js when nothing is published.
  const marketingVideo = useVideo(
    'practicums.marketing',
    videos.marketing.embed ? { embed: videos.marketing.embed, caption: videos.marketing.caption } : null
  );
  const fullCaseVideo = useVideo(
    'practicums.fullCase',
    videos.fullCase.embed ? { embed: videos.fullCase.embed, caption: videos.fullCase.caption } : null
  );
  const caseBuildingVideo = useVideo(
    'practicums.caseBuilding',
    videos.caseBuilding.embed ? { embed: videos.caseBuilding.embed, caption: videos.caseBuilding.caption } : null
  );

  return (
    <>
      <Seo
        title="Practicums"
        description="The exam room, simulated. A patient who converses, an evaluator who scores, separately: OSCE-style clinical skills encounters with photorealistic patient avatars, disclosure-gated histories, verbalized examination, and key-feature scoring with instructor override."
      />

      {/* Hero: claim left, marketing film right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-center gap-14 py-20 md:py-28 lg:grid-cols-[1fr_1.05fr] lg:gap-12">
          <div>
            <Mark dark>Practicums · For students and programs</Mark>
            <h1 className="hero-rise mt-4 font-serif text-4xl leading-[1.06] md:text-5xl">
              The exam room, simulated.
            </h1>
            <p className="hero-rise-2 mt-6 max-w-measure text-lg leading-relaxed text-slate-dark-text">
              A patient who converses. An evaluator who scores. Separately. OSCE-style clinical skills assessment
              capacity, without standardized-patient scheduling and cost.
            </p>
            <div className="hero-rise-3 mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary-on-dark">See a simulated encounter</Link>
              <a href="#anatomy" className="btn-secondary-on-dark">How a station works</a>
            </div>
          </div>
          <div className="hero-rise-3">
            <VideoSlot
              video={marketingVideo}
              title={videos.marketing.title}
              text="The ninety-second film is ready: paste its YouTube embed URL into src/data/practicumsData.js and it plays here."
            />
          </div>
        </Container>
      </section>

      {/* Two intelligences + interactive miniature */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid items-start gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
            <div>
              <Mark>Two intelligences, separated</Mark>
              <h2 className="mt-3 max-w-[20ch] font-serif text-3xl leading-tight md:text-4xl">
                The actor never grades the candidate.
              </h2>
              <div className="mt-8 space-y-7">
                {twoIntelligences.map((t) => (
                  <div key={t.name}>
                    <h3 className="flex items-start gap-2 font-serif text-lg text-bright">
                      <span aria-hidden="true" className="mt-2.5 inline-block h-2 w-2 shrink-0 rounded-[1px] bg-seal" />
                      {t.name}
                    </h3>
                    <p className="mt-2 leading-relaxed text-slate">{t.text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm leading-relaxed text-faint">
                Try the miniature on the right: general questions get general answers, the discriminating question
                opens the gate, and a separate evaluator scores the transcript afterward, hurdles first.
              </p>
            </div>
            <MiniEncounter />
          </div>
        </Container>
      </section>

      {/* Anatomy of an encounter + full case film */}
      <section id="anatomy" className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>Anatomy of an encounter</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            From the door instruction to the scorecard.
          </h2>
          <ol className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {encounterAnatomy.map((step, i) => (
              <li key={step.name} className="flex gap-5">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-seal/60 text-sm text-seal"
                >
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-bright">{step.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-14 lg:max-w-measure-wide">
            <h3 className="font-serif text-xl text-bright">{videos.fullCase.title}</h3>
            <div className="mt-4">
              <VideoSlot
                video={fullCaseVideo}
                title={videos.fullCase.title}
                text="The full-encounter film is ready: paste its YouTube embed URL into src/data/practicumsData.js and it plays here."
              />
            </div>
          </div>
        </Container>
      </section>

      {/* Scoring */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>How scoring works</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            A good-feeling encounter can still be marked down. The report explains why, in the right order.
          </h2>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-2">
            {scoringPoints.map((p) => (
              <div key={p.title} className="border-t border-hairline pt-5">
                <h3 className="font-serif text-lg text-bright">{p.title}</h3>
                <p className="mt-2 leading-relaxed text-slate">{p.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-measure-wide text-sm leading-relaxed text-faint">
            Competency ratings run on a five-anchor scale, from unacceptable to above the expected level, and the
            reference point is a recent graduate accepted into postgraduate training, not a consultant. Acceptable is a
            good outcome, and the product says so.
          </p>
        </Container>
      </section>

      {/* Case building + case-building film */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-28">
        <Container>
          <div className="grid items-start gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
            <div>
              <Mark dark>For programs: building a station</Mark>
              <h2 className="mt-3 max-w-[20ch] font-serif text-3xl leading-tight md:text-4xl">
                A case is a curriculum object.
              </h2>
              <div className="mt-8 space-y-7">
                {caseAuthoring.map((c) => (
                  <div key={c.name}>
                    <h3 className="flex items-start gap-2 font-serif text-lg text-bright">
                      <span aria-hidden="true" className="mt-2.5 inline-block h-2 w-2 shrink-0 rounded-[1px] bg-seal" />
                      {c.name}
                    </h3>
                    <p className="mt-2 leading-relaxed text-slate-dark-text">{c.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pt-10">
              <h3 className="font-serif text-xl text-bright">{videos.caseBuilding.title}</h3>
              <div className="mt-4">
                <VideoSlot
                  video={caseBuildingVideo}
                  title={videos.caseBuilding.title}
                  text="The case-building film is ready: paste its YouTube embed URL into src/data/practicumsData.js and it plays here."
                />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Honest scope */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>What we claim, and what we do not</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Said plainly, because you will check.
          </h2>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-2">
            {honestScope.map((h) => (
              <div key={h.title} className="border-t border-hairline pt-5">
                <h3 className="font-serif text-lg text-bright">{h.title}</h3>
                <p className="mt-2 leading-relaxed text-slate">{h.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Bridge to MedSchool */}
      <section className="pb-16 md:pb-20">
        <Container>
          <div className="flex flex-col items-start justify-between gap-6 rounded-media border border-hairline bg-surface p-8 md:flex-row md:items-center md:p-10">
            <div>
              <h2 className="font-serif text-2xl text-bright">Encounters land in the institutional record.</h2>
              <p className="mt-2 max-w-measure-wide leading-relaxed text-slate">
                Practicums integrates natively with EdAI MedSchool: stations map to the curriculum, and the competency
                evidence each encounter generates lands in the same record a school shows its accreditor.
              </p>
            </div>
            <Link to="/products/medschool" className="btn-secondary shrink-0">
              Explore EdAI MedSchool
            </Link>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="border-t border-hairline py-20 md:py-28">
        <Container>
          <Mark>Questions programs ask</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
          <div className="mt-10 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
            {faq.map((item, i) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <span className="font-semibold text-bright">{item.q}</span>
                  <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-hairline bg-ink-deep/40 py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            One unasked question can undo an encounter. Practice asking it.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">See a simulated encounter</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
